import { cn } from "@/utils";
import React, { ReactNode } from "react";
import { Checkbox } from "./Checkbox";

export type CheckboxGroupOption = {
  label: string | ReactNode;
  value?: boolean;
  disabled?: boolean;
};

type CheckboxGroupProps = {
  ref?: React.Ref<HTMLDivElement>;
  options: CheckboxGroupOption[];
  value?: boolean[];
  onChange?: (values: boolean[]) => void;
  onAfterChange?: (values: boolean[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
  label?: string;
  error?: string;
  required?: boolean;
  className?: string;
  classNameContainer?: string;
  classNameCheckbox?: string;
  checkboxType?: "dark" | "white";
  direction?: "horizontal" | "vertical";
  checkboxPosition?: "before" | "after";
};

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  ref,
  options,
  value = [],
  onChange,
  disabled = false,
  readOnly = false,
  label,
  error,
  required = false,
  className,
  checkboxType = "white",
  direction = "horizontal",
  checkboxPosition = "after",
  classNameContainer,
  classNameCheckbox,
  onAfterChange,
}) => {
  // Initialize value array with default values if empty
  const initializedValue =
    value.length === 0 ? options.map((option) => option.value || false) : value;

  const handleChange = (optionIndex: number, checked: boolean) => {
    if (disabled || readOnly) return;

    const newValues = [...initializedValue];
    newValues[optionIndex] = checked;

    onChange?.(newValues);
    onAfterChange?.(newValues);
  };

  const isChecked = (optionIndex: number) => {
    if (value[optionIndex] !== undefined) {
      return value[optionIndex];
    }
    return options[optionIndex]?.value || false;
  };

  const renderCheckboxAndLabel = (
    option: CheckboxGroupOption,
    isOptionDisabled: boolean,
    checked: boolean
  ) => {
    const checkboxElement = (
      <Checkbox
        type={checkboxType}
        checked={checked}
        disabled={isOptionDisabled}
        onChange={(e) =>
          handleChange(options.indexOf(option), e.target.checked)
        }
        aria-label={option.label?.toString()}
        className={cn("min-w-[18px] min-h-[18px]", classNameCheckbox)}
        readOnly={readOnly}
      />
    );

    const labelElement = <div className="min-w-0 flex-1">{option.label}</div>;

    return (
      <>
        {checkboxElement}
        {labelElement}
      </>
    );
  };

  return (
    <div ref={ref} className={cn("relative", classNameContainer)}>
      {label && (
        <label
          className={cn(
            "absolute left-4 top-0 -translate-y-1/2 px-1 text-xs z-[2] bg-white",
            {
              "text-error_main": error,
              "text-grey-600": !error,
            }
          )}
          style={{
            background: disabled
              ? "linear-gradient(to bottom, transparent 50%, #F4F6F8 50%)"
              : readOnly
              ? "linear-gradient(to bottom, transparent 50%, #FAFAFA 50%)"
              : "#ffffff",
          }}
        >
          {label}
          {required && <span className="text-error_main ml-1">*</span>}
        </label>
      )}

      <div
        className={cn("flex gap-4", {
          "flex-wrap": direction === "horizontal",
          "flex-col": direction === "vertical",
          "items-center": direction === "horizontal",
          "items-start": direction === "vertical",
        })}
      >
        {options.map((option, index) => {
          const isOptionDisabled = option.disabled || disabled;
          const checked = isChecked(index);

          return (
            <label
              key={`${index}-${option.value}`}
              className={cn(
                "flex text-sm text-light_primary gap-2 min-w-0",
                {
                  "cursor-not-allowed": isOptionDisabled,
                  "cursor-pointer": !isOptionDisabled && !readOnly,
                  "cursor-default": readOnly,
                  "flex-row-reverse": checkboxPosition === "after",
                  "items-center": direction === "horizontal",
                  "items-start w-full": direction === "vertical",
                },
                className
              )}
            >
              {renderCheckboxAndLabel(option, isOptionDisabled, checked)}
            </label>
          );
        })}
      </div>

      {error && (
        <p className="text-error_main text-xs mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export { CheckboxGroup };
